import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * Toast unificado — notificações não-bloqueantes (snackbar).
 * Auto-dismiss, empilhável, acessível (role="alert" + aria-live).
 * Consolidado: /components/ui/Toast.js + /components/cliente/ToastProvider.js (REMOVIDO)
 *
 * Uso:
 *   const toast = useToast();
 *   toast.success('NF emitida!');
 *   toast.error('Falhou: ' + msg);
 *   toast.info('Processando…', { duracao: 0 });  // 0 = persistente
 */

const ToastContext = createContext(null);
let _seq = 0;

function iconePara(tipo) {
  switch (tipo) {
    case 'success': return '✓';
    case 'error': return '✕';
    case 'warning': return '⚠';
    default: return 'ℹ';
  }
}

function Toaster({ toasts, onDismiss }) {
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(
    <div className="ui-toaster" role="region" aria-live="polite" aria-label="Notificações">
      {toasts.map(t => (
        <div key={t.id} className={`ui-toast ui-toast--${t.tipo}`} role="alert">
          <span className="ui-toast__icon" aria-hidden="true">{iconePara(t.tipo)}</span>
          <div className="ui-toast__body">
            {t.titulo && <div className="ui-toast__title">{t.titulo}</div>}
            <div className="ui-toast__msg">{t.mensagem}</div>
          </div>
          <button
            className="ui-toast__close"
            aria-label="Fechar notificação"
            onClick={() => onDismiss(t.id)}
          >×</button>
        </div>
      ))}
    </div>,
    document.body,
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts(ts => ts.filter(x => x.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const push = useCallback((tipo, mensagem, opts = {}) => {
    const id = ++_seq;
    const duracao = opts.duracao != null ? opts.duracao : (tipo === 'error' ? 6500 : 4000);
    setToasts(ts => [...ts, { id, tipo, mensagem: String(mensagem ?? ''), titulo: opts.titulo }]);
    if (duracao > 0) {
      timers.current[id] = setTimeout(() => dismiss(id), duracao);
    }
    return id;
  }, [dismiss]);

  const api = useMemo(() => ({
    success: (m, o) => push('success', m, o),
    error: (m, o) => push('error', m, o),
    warning: (m, o) => push('warning', m, o),
    info: (m, o) => push('info', m, o),
    show: (m, o) => push((o && o.tipo) || 'info', m, o),
    dismiss,
  }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast precisa estar dentro de <ToastProvider>');
  return ctx;
}

export default ToastProvider;
